import React from "react";
import { createRoot } from "react-dom/client";
import App from "./App";
import "./index.css";

// Q1 — one-time reload guard for stale lazy chunks. After a `--delete` deploy,
// a page loaded before the deploy can request a hashed chunk that no longer
// exists on the box (404); Vite's dynamic import then throws "Failed to fetch
// dynamically imported module" / a ChunkLoadError. A single hard reload pulls
// the fresh index + chunk graph. The sessionStorage flag makes the reload
// strictly one-shot, so a genuine persistent import bug surfaces instead of
// infinite-reloading.
(() => {
  const RELOAD_FLAG = "__pe_chunk_reloaded";
  const isChunkLoadFailure = (reason: unknown): boolean => {
    if (!reason) return false;
    const name = (reason as { name?: string }).name || "";
    const message = (reason as { message?: string }).message || String(reason);
    return (
      name === "ChunkLoadError" ||
      /Failed to fetch dynamically imported module/i.test(message) ||
      /error loading dynamically imported module/i.test(message) ||
      /importing a module script failed/i.test(message)
    );
  };
  const handleChunkFailure = (reason: unknown) => {
    if (!isChunkLoadFailure(reason)) return;
    if (sessionStorage.getItem(RELOAD_FLAG)) return; // already retried — let it surface
    sessionStorage.setItem(RELOAD_FLAG, "1");
    window.location.reload();
  };
  window.addEventListener("error", (e) => handleChunkFailure(e.error || e.message));
  window.addEventListener("unhandledrejection", (e) => handleChunkFailure(e.reason));
})();

createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);
